import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import {
  useActingRepresentatives,
  useClientAccounts,
  useClientActivityEvents,
  useClientCalls,
  useClientFiles,
  useClientFlowInstances,
  useClientGroupMembers,
  useClientMandateOrders,
  useClientMandates,
  useClientMeetings,
  useClientNotes,
  useClientOrders,
  useClientParties,
  useClientRecurringOrders,
  useClientSavingsPlans,
} from "@/lib/hooks/use-client-detail";
import { useT } from "@/lib/i18n/use-t";
import type { ClientType } from "@/lib/types/client";
import { AccountsSection } from "./accounts-section";
import {
  type ActivityCreateTarget,
  type ActivityOpenTarget,
  ActivitySection,
} from "./activity-section";
import { ClientFlowsSection } from "./client-flows-section";
import { ClientPartiesSection } from "./client-parties-section";
import { T } from "./client-tabs.i18n";
import { GroupMembersSection } from "./group-members-section";
import { MandateOrdersSection } from "./mandate-orders-section";
import { MandatesSection } from "./mandates-section";
import {
  type ClientRecordKind,
  type EditableRecordKind,
  NotesAndFilesSection,
} from "./notes-and-files";
import { OrdersSection } from "./orders-section";
import { RecurringOrdersSection } from "./recurring-orders-section";
import { SavingsPlansSection } from "./savings-plans-section";

// QL-125 — notes, calls, meetings and files share one tab. The AI chat widgets
// (note/call/meeting-widget.tsx) and older bookmarks link with
// `?tab=<kind>&id=…`, so the kind doubles as the tab param and lands on the
// combined tab with that entry open.
const RECORD_TAB_KINDS: Record<string, EditableRecordKind> = {
  notes: "note",
  calls: "call",
  meetings: "meeting",
};
const NOTES_AND_FILES_TAB = "notes";
const TAB_ALIASES = {
  calls: NOTES_AND_FILES_TAB,
  meetings: NOTES_AND_FILES_TAB,
  files: NOTES_AND_FILES_TAB,
} as const;

const TAB_VALUES = [
  "activity",
  "members",
  "parties",
  "flows",
  NOTES_AND_FILES_TAB,
  "accounts",
  "savings-plans",
  "mandates",
  "orders",
  "recurring-orders",
  "mandate-orders",
] as const;
type ClientTab = (typeof TAB_VALUES)[number];

type ClientTabsProps = {
  clientId: string;
  /** QP-184 — drives the Members tab (Group only) and the parties tab label. */
  clientType: ClientType;
  /** QL-62 — the eID fallback when a related party has no country of its own. */
  clientCountry: string | null;
};

export function ClientTabs({ clientId, clientType, clientCountry }: ClientTabsProps) {
  const t = useT(T);
  const { flowInstances, mutate: mutateFlowInstances } = useClientFlowInstances(clientId);
  const { activityEvents } = useClientActivityEvents(clientId);
  const { accounts } = useClientAccounts(clientId);
  const { savingsPlans } = useClientSavingsPlans(clientId);
  const { mandates } = useClientMandates(clientId);
  const { notes, isLoading: notesLoading, mutate: mutateNotes } = useClientNotes(clientId);
  const { calls, isLoading: callsLoading, mutate: mutateCalls } = useClientCalls(clientId);
  const {
    meetings,
    isLoading: meetingsLoading,
    mutate: mutateMeetings,
  } = useClientMeetings(clientId);
  const { files, isLoading: filesLoading, mutate: mutateFiles } = useClientFiles(clientId);
  // QP-184 — group members (Group clients only) + related parties tabs.
  const isGroup = clientType === "Group";
  const { members } = useClientGroupMembers(isGroup ? clientId : null);
  const { parties } = useClientParties(clientId);
  const { orders, mutate: mutateOrders } = useClientOrders(clientId);
  // QL-150 step 4 — tab renders only when recurring orders exist: tenants
  // without the recurring-orders gate never see it (no data can exist), and
  // history stays visible if the gate is later turned off.
  const { recurringOrders, mutate: mutateRecurringOrders } = useClientRecurringOrders(clientId);
  const hasRecurringOrders = recurringOrders.flowInstances.length > 0;
  // QL-375 group 9 — same rule for mandate orders (Notion row 5: copy the
  // Recurring orders pattern): the tab exists only once an order exists.
  const { mandateOrders, mutate: mutateMandateOrders } = useClientMandateOrders(clientId);
  const hasMandateOrders = mandateOrders.flowInstances.length > 0;
  // QPBI-387 — count the in-group representative (from Members) in the tab
  // label; a party-sourced representative is already a row in `parties`.
  const { representatives } = useActingRepresentatives(isGroup ? clientId : null);
  const partiesCount =
    parties.length + representatives.filter((r) => r.source === "group_member").length;
  const partiesLabel =
    clientType === "Company"
      ? t.partiesCompany
      : clientType === "Group"
        ? t.partiesGroup
        : t.partiesPerson;

  // Tabs shown only for some clients; a `?tab=` naming one of them while it is
  // hidden (or before its data has loaded) shows Activity instead.
  const hiddenTabs: Partial<Record<ClientTab, boolean>> = {
    members: !isGroup,
    "savings-plans": savingsPlans.length === 0,
    mandates: mandates.length === 0,
    "recurring-orders": !hasRecurringOrders,
    "mandate-orders": !hasMandateOrders,
  };
  const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, "activity", {
    aliases: TAB_ALIASES,
    isAvailable: (tab) => !hiddenTabs[tab],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const idFromUrl = searchParams.get("id");
  // Bumping this returns the Notes & files section to its list view.
  const [recordsResetKey, setRecordsResetKey] = useState(0);

  const [autoCreateFlow, setAutoCreateFlow] = useState(false);
  // QL-133 — the Representative tab hands editing of an acting member back to
  // the Members tab, which owns the member dialog. QL-654: with several flagged
  // members it is THAT member (by id) that opens.
  const [editRepresentativeMemberId, setEditRepresentativeMemberId] = useState<string | null>(null);
  const [autoCreateRecord, setAutoCreateRecord] = useState<EditableRecordKind | null>(null);
  const [autoEditRecord, setAutoEditRecord] = useState<{
    kind: EditableRecordKind;
    id: string;
  } | null>(null);

  // Sync ?id= from URL into autoEdit state, then strip the param so back-nav doesn't re-trigger
  useEffect(() => {
    if (!idFromUrl || !tabFromUrl) return;
    if (!Object.hasOwn(RECORD_TAB_KINDS, tabFromUrl)) return;
    const kind = RECORD_TAB_KINDS[tabFromUrl];
    setAutoEditRecord({ kind, id: idFromUrl });
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }, [idFromUrl, tabFromUrl, searchParams, setSearchParams]);

  const resetIfActive = (tab: string, bump: () => void) => {
    if (activeTab === tab) bump();
  };

  const handleCreate = useCallback(
    (target: ActivityCreateTarget) => {
      if (target === "flow") {
        setActiveTab("flows");
        setAutoCreateFlow(true);
        return;
      }
      setActiveTab(NOTES_AND_FILES_TAB);
      setRecordsResetKey((k) => k + 1);
      setAutoCreateRecord(target);
    },
    [setActiveTab],
  );

  const handleOpen = useCallback(
    (target: ActivityOpenTarget, id: string) => {
      setActiveTab(NOTES_AND_FILES_TAB);
      setRecordsResetKey((k) => k + 1);
      setAutoEditRecord({ kind: target, id });
    },
    [setActiveTab],
  );

  const mutateRecords = useCallback(
    (kind: ClientRecordKind) => {
      if (kind === "note") return mutateNotes();
      if (kind === "call") return mutateCalls();
      if (kind === "meeting") return mutateMeetings();
      return mutateFiles();
    },
    [mutateNotes, mutateCalls, mutateMeetings, mutateFiles],
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList>
        <TabsTrigger value="activity">{t.activity}</TabsTrigger>
        {isGroup && (
          <TabsTrigger value="members">
            {t.members} ({members.length})
          </TabsTrigger>
        )}
        <TabsTrigger value="parties">
          {partiesLabel} ({partiesCount})
        </TabsTrigger>
        <TabsTrigger value="flows">{t.flows}</TabsTrigger>
        <TabsTrigger
          value={NOTES_AND_FILES_TAB}
          onClick={() => resetIfActive(NOTES_AND_FILES_TAB, () => setRecordsResetKey((k) => k + 1))}
        >
          {t.notesAndFiles}
        </TabsTrigger>
        <TabsTrigger value="accounts">{t.accounts}</TabsTrigger>
        {savingsPlans.length > 0 && (
          <TabsTrigger value="savings-plans">{t.savingsPlans}</TabsTrigger>
        )}
        {mandates.length > 0 && <TabsTrigger value="mandates">{t.mandates}</TabsTrigger>}
        <TabsTrigger value="orders">{t.orders}</TabsTrigger>
        {hasRecurringOrders && (
          <TabsTrigger value="recurring-orders">{t.recurringOrders}</TabsTrigger>
        )}
        {hasMandateOrders && <TabsTrigger value="mandate-orders">{t.mandateOrders}</TabsTrigger>}
      </TabsList>

      <TabsContent value="activity" className="mt-6">
        <ActivitySection
          clientId={clientId}
          notes={notes}
          calls={calls}
          meetings={meetings}
          events={activityEvents}
          onCreate={handleCreate}
          onOpen={handleOpen}
        />
      </TabsContent>

      {isGroup && (
        <TabsContent value="members" className="mt-6">
          <GroupMembersSection
            clientId={clientId}
            autoEditMemberId={editRepresentativeMemberId}
            onAutoEditMemberConsumed={() => setEditRepresentativeMemberId(null)}
          />
        </TabsContent>
      )}

      <TabsContent value="parties" className="mt-6">
        <ClientPartiesSection
          clientId={clientId}
          clientType={clientType}
          clientCountry={clientCountry}
          onEditGroupMember={(memberId) => {
            setEditRepresentativeMemberId(memberId);
            setActiveTab("members");
          }}
        />
      </TabsContent>

      <TabsContent value="flows" className="mt-6">
        <ClientFlowsSection
          clientId={clientId}
          instances={flowInstances}
          onMutate={mutateFlowInstances}
          autoOpenDialog={autoCreateFlow}
          onAutoOpenDialogConsumed={() => setAutoCreateFlow(false)}
        />
      </TabsContent>

      <TabsContent value="accounts" className="mt-6">
        <AccountsSection clientId={clientId} accounts={accounts} />
      </TabsContent>

      {/* QL-150 step 2 — savings plans on their own tab (user decision
          2026-08-28: accounts stay a flat list; goal badges carry the
          mapping). Tab hidden when the client has no plans. */}
      <TabsContent value="savings-plans" className="mt-6">
        <SavingsPlansSection clientId={clientId} savingsPlans={savingsPlans} accounts={accounts} />
      </TabsContent>

      {/* QL-375 — the client's bank mandates, read-only; tab hidden when there are none. */}
      <TabsContent value="mandates" className="mt-6">
        <MandatesSection clientId={clientId} mandates={mandates} />
      </TabsContent>

      <TabsContent value={NOTES_AND_FILES_TAB} className="mt-6">
        <NotesAndFilesSection
          resetSignal={recordsResetKey}
          clientId={clientId}
          notes={notes}
          calls={calls}
          meetings={meetings}
          files={files}
          isLoading={notesLoading || callsLoading || meetingsLoading || filesLoading}
          onMutate={mutateRecords}
          autoCreate={autoCreateRecord}
          onAutoCreateConsumed={() => setAutoCreateRecord(null)}
          autoEdit={autoEditRecord}
          onAutoEditConsumed={() => setAutoEditRecord(null)}
        />
      </TabsContent>

      <TabsContent value="orders" className="mt-6">
        <OrdersSection clientId={clientId} data={orders} onMutate={mutateOrders} />
      </TabsContent>

      <TabsContent value="recurring-orders" className="mt-6">
        <RecurringOrdersSection data={recurringOrders} onMutate={mutateRecurringOrders} />
      </TabsContent>

      <TabsContent value="mandate-orders" className="mt-6">
        <MandateOrdersSection data={mandateOrders} onMutate={mutateMandateOrders} />
      </TabsContent>
    </Tabs>
  );
}
